import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/lib/constants";

export default function Navbar({ onOpenConsult }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0A1628]/90 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group shrink-0 min-w-0">
          <img
            src="/brand/unexx-mark.png"
            alt="U-NEXX"
            className="h-12 md:h-14 w-auto shrink-0 object-contain"
          />
          <div className="hidden sm:block leading-tight border-l border-white/15 pl-3 min-w-0">
            <div className="font-serif-display text-[#F3EFE4] text-base md:text-lg tracking-wide">U-NEXX</div>
            <div className="text-[0.55rem] tracking-[0.32em] text-[#8FA3B8] uppercase whitespace-nowrap">Development Group</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((link) =>
            link.external ? (
              <a
                key={link.href}
                href={link.href}
                data-testid={link.testId}
                className="nav-link"
              >
                {link.label}
              </a>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                end={link.to === "/"}
              >
                {link.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="navbar-book-consult"
            onClick={onOpenConsult}
            className="hidden md:inline-flex btn-primary !py-3 !px-5 !text-[0.72rem]"
          >
            Book Consultation
            <span className="btn-arrow">→</span>
          </button>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A1628] border-t border-white/5">
          <nav className="flex flex-col px-6 py-8 gap-6">
            {NAV_LINKS.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-${link.testId}`}
                  className="nav-link text-sm"
                >
                  {link.label}
                </a>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-${link.testId}`}
                  className="nav-link text-sm"
                  end={link.to === "/"}
                >
                  {link.label}
                </NavLink>
              )
            )}
            <button
              type="button"
              data-testid="mobile-book-consult"
              onClick={() => {
                setOpen(false);
                onOpenConsult();
              }}
              className="btn-primary w-fit"
            >
              Book Consultation
              <span className="btn-arrow">→</span>
            </button>
            <div className="text-xs text-[#8FA3B8] mt-2">
              {BRAND.phone} · {BRAND.email}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
